// src/services/admin/adminAwards.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const adminAwardsApi = createApi({
  reducerPath: "adminAwardsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}/api/admin/adminAwards`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["AdminAwards", "AdminAwardAssign"],
  endpoints: (builder) => ({
        setAwardCompleteDate: builder.mutation({
          query: ({ id, complete_date }) => ({
            url: `/set-complete-date/${id}`,
            method: "PUT",
            body: { complete_date },
          }),
          invalidatesTags: (result, error, { id }) => [
            { type: "AdminAwards", id },
            { type: "AdminAwards", id: "PARTIAL-LIST" },
          ],
        }),
    // T4A summary endpoint
    getT4ASummary: builder.query({
      query: (year) => `/t4a-summary?year=${year}`,
      providesTags: (result) => {
        // Support both array and { data: array } response
        const rows = Array.isArray(result)
          ? result
          : result && Array.isArray(result.data)
            ? result.data
            : [];
        if (Array.isArray(rows)) {
          return rows.map((row) => ({
            type: "AdminAwards",
            id: row.sin || row.id,
          }));
        }
        return [{ type: "AdminAwards", id: "T4A-SUMMARY" }];
      },
    }),
    getAwardsView: builder.query({
      query: ({
        page,
        limit,
        awardYear,
        name,
        email,
        status,
        claimDate,
        completeDate,
        selected_by_type_id,
        schoolId,
      }) => {
        let url = `/?page=${page}&limit=${limit}`;
        if (awardYear) url += `&awardYear=${awardYear}`;
        if (name) url += `&name=${encodeURIComponent(name)}`;
        if (email) url += `&email=${encodeURIComponent(email)}`;
        if (status) url += `&status=${encodeURIComponent(status)}`;
        if (claimDate) url += `&claimDate=${encodeURIComponent(claimDate)}`;
        if (completeDate)
          url += `&completeDate=${encodeURIComponent(completeDate)}`;
        if (selected_by_type_id) {
          url += `&selected_by_type_id=${encodeURIComponent(selected_by_type_id)}`;
        }
        if (schoolId) {
          url += `&schoolId=${encodeURIComponent(schoolId)}`;
          url += `&school_id=${encodeURIComponent(schoolId)}`;
        }
        return url;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "AdminAwards", id })),
              { type: "AdminAwards", id: "PARTIAL-LIST" },
            ]
          : [{ type: "AdminAwards", id: "PARTIAL-LIST" }],
    }),
    getAwardList: builder.query({
      query: ({
        page = 1,
        limit = 50,
        awardYear,
        school_presented_at_id,
        city_id,
        selected_by_type_id,
        scholarship_name,
        contact_name,
        contact_email,
      }) => {
        let url = `/awardList?page=${page}&limit=${limit}`;
        if (awardYear) url += `&awardYear=${awardYear}`;
        if (school_presented_at_id)
          url += `&presentedAt=${school_presented_at_id}`;
        if (city_id) url += `&city=${city_id}`;
        if (selected_by_type_id)
          url += `&selected_by_type_id=${selected_by_type_id}`;
        if (scholarship_name)
          url += `&scholarship_name=${encodeURIComponent(scholarship_name)}`;
        if (contact_name)
          url += `&contact_name=${encodeURIComponent(contact_name)}`;
        if (contact_email)
          url += `&contact_email=${encodeURIComponent(contact_email)}`;
        return url;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "AdminAwards", id })),
              { type: "AdminAwards", id: "PARTIAL-LIST" },
            ]
          : [{ type: "AdminAwards", id: "PARTIAL-LIST" }],
    }),

    getAwardById: builder.query({
      query: (id) => `/get-one-by-id/${id}`,
      providesTags: (result, error, id) => [{ type: "AdminAwards", id }],
    }),
    updateAward: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/update-one-by-id/${id}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminAwards", id },
        { type: "AdminAwards", id: "PARTIAL-LIST" },
      ],
    }),
    awardScholarship: builder.mutation({
      query: ({ studentId, scholarshipId, formData }) => ({
        url: `/awardScholarship/${studentId}/${scholarshipId}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminAwards", id },
        { type: "AdminAwards", id: "PARTIAL-LIST" },
      ],
    }),
    deleteFile: builder.mutation({
      query: (id) => ({
        url: `/delete-one-by-id/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AdminAwards", id },
        { type: "AdminAwards", id: "PARTIAL-LIST" },
      ],
    }),
    viewFile: builder.query({
      query: (id) => ({
        url: `/view-file/${id}`,
        method: "GET",
      }),
    }),
    downloadFile: builder.query({
      query: (id) => ({
        url: `/download-file/${id}`,
        method: "GET",
      }),
    }),
    getAwardsForCertificates: builder.query({
      query: ({ awardYear, presentedAt, city }) => {
        let url = `/get-awards-for-certificates`;
        const params = [];
        if (awardYear) params.push(`awardYear=${awardYear}`);
        if (presentedAt) params.push(`presentedAt=${presentedAt}`);
        if (city) params.push(`city=${city}`);

        if (params.length > 0) url += `?${params.join("&")}`;
        return url;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "AdminAwards", id })),
              { type: "AdminAwards", id: "PARTIAL-LIST" },
            ]
          : [{ type: "AdminAwards", id: "PARTIAL-LIST" }],
    }),
    updateSortOrder: builder.mutation({
      query: (order) => ({
        url: `/update-sort-order`,
        method: "PUT",
        body: order,
      }),
      invalidatesTags: [{ type: "AdminAwards", id: "PARTIAL-LIST" }],
    }),
    assignAward: builder.mutation({
      query: (payload) => ({
        url: "/assign",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "AdminAwardAssign", id: "LIST" },
        { type: "AdminAwards", id: "PARTIAL-LIST" },
      ],
    }),
    unassignAward: builder.mutation({
      query: ({ awardID }) => ({
        url: `/unassign/${awardID}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "AdminAwardAssign", id: "LIST" },
        { type: "AdminAwards", id: "PARTIAL-LIST" },
      ],
    }),
    getAllAwardWinners: builder.query({
      query: ({
        year,
        city_id,
        award_name,
        student_name,
        selected_by,
        selected_by_type_id,
        school_presented_at_id,
        school_id,
        email,
        page = 1, // Default page number
        limit = 50, // Default limit per page
      } = {}) => {
        let url = "/get-all-winners";

        // Create a new URLSearchParams instance to build the query string
        const params = new URLSearchParams();

        // Append the filters and pagination to the params
        if (year) {
          params.append("year", year);
        }
        if (award_name) {
          params.append("award_name", award_name);
        }
        if (student_name) {
          params.append("student_name", student_name);
        }
        if (selected_by) {
          params.append("selected_by", selected_by);
        }
        if (email) {
          params.append("email", email);
        }

        if (selected_by_type_id) {
          params.append("selected_by_type_id", selected_by_type_id);
        }
        if (school_id) {
          params.append("school_id", school_id);
        }
        if (school_presented_at_id) {
          params.append("school_presented_at_id", school_presented_at_id);
        }
        if (city_id) {
          params.append("city_id", city_id);
        }

        // Add pagination parameters
        params.append("page", page);
        params.append("limit", limit);

        // Append the query parameters to the URL
        url += `?${params.toString()}`;

        return url;
      },
      providesTags: [{ type: "AdminAwards", id: "AWARD-WINNERS" }],
    }),
  }),
});

export const {
  useGetAwardsViewQuery,
  useGetAwardListQuery,
  useGetAwardByIdQuery,
  useUpdateAwardMutation,
  useDeleteFileMutation,
  useDownloadFileQuery,
  useLazyDownloadFileQuery,
  useViewFileQuery,
  useLazyViewFileQuery,
  useGetAwardsForCertificatesQuery,
  useUpdateSortOrderMutation,
  useAssignAwardMutation,
  useUnassignAwardMutation,
  useGetAllAwardWinnersQuery,
  useGetT4ASummaryQuery,
  useSetAwardCompleteDateMutation,
} = adminAwardsApi;
export default adminAwardsApi;
export { adminAwardsApi };
